import React, {
 useCallback, useEffect, useMemo, useRef, useState
} from 'react'
import {
  FocusContext,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { useNavigate, useParams, useLocation } from 'react-router'
import { useAppSelector } from '../../hooks/useStore'
import StationPassportTitle from '../../components/StationPassportTitle/StationPassportTitle'
import tts from '../../lib/TTS'
import * as UI from '../../lib/ui'
import googleAnalytics4 from '../../lib/googleAnalytics'
import {
  KeyTypeEnum,
  findKeyEvent,
} from '../../../../shared/providers/KeyDownProvider'
import type { Membership, PassportStation } from './types'

const DEFAULT_LEARN_MORE_URL = 'https://pbs.org/passport/learn-more'

function PassPortUpsellTS() {
  const navigate = useNavigate()
  const { callsign } = useParams<{ callsign: string }>()
  const location = useLocation()

  const station = useAppSelector((state) => state.station)
  const memberships = useAppSelector((state) => state.user.memberships)
  const deeplinkedTo = useAppSelector((state) => state.deepLink?.deeplinkedTo)

  const { ref, focusKey, focusSelf } = useFocusable()

  const [shortCommonName, setShortCommonName] = useState<string | null>(null)

  const isMembershipRoute = location.search.indexOf('membership=true') !== -1

  const resolvedStation: PassportStation | Membership = useMemo(() => {
    if (isMembershipRoute) {
      const matched = (memberships as Membership[])?.find(
        (m) => m.call_sign === callsign
      )
      return matched ?? station
    }
    return station
  }, [isMembershipRoute, memberships, callsign, station])

  const isVideoUpsell = useMemo(
    () => location.search?.indexOf('?video=') !== -1,
    [location.search]
  )

  const goBackUrl = (location.state as { goBackParameterUrl?: string })
    ?.goBackParameterUrl

  const learnMoreUrlRef = useRef<string>('')

  // Resolve learnMoreUrl and shortCommonName on mount
  useEffect(() => {
    if (resolvedStation.call_sign === station.call_sign) {
      learnMoreUrlRef.current =
        resolvedStation.passport_learn_more_url || DEFAULT_LEARN_MORE_URL
      setShortCommonName(resolvedStation.short_common_name)
    } else {
      const membership = (memberships as Membership[])?.find(
        (m) => m.call_sign === resolvedStation.call_sign
      )
      if (membership) {
        learnMoreUrlRef.current =
          membership.passport_learn_more_url ||
          membership.website_url ||
          DEFAULT_LEARN_MORE_URL
        setShortCommonName(membership.short_common_name)
      }
    }
  }, [resolvedStation, station, memberships])

  // Hide side panel + GA4 page view on mount
  useEffect(() => {
    UI.setSidePanelVisibility(false)
    googleAnalytics4.sendPageViewEvent(
      `Passport Handoff - ${isVideoUpsell ? 'Video' : 'Other'}`
    )
  }, [isVideoUpsell])

  // Bridge workaround for legacy focus system coexistence
  // TODO: remove when whole app is migrated to norigin spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  // Focus + TTS when shortCommonName resolves
  useEffect(() => {
    if (shortCommonName) {
      focusSelf()

      const videoUpsellCopy = isVideoUpsell
        ? `Watch this video with  ${shortCommonName} Passport...`
        : ''

      tts.addTextToRead(
        `${shortCommonName} Passport... ${videoUpsellCopy} ${shortCommonName} Passport is an additional member benefit that provides extended access to quality PBS streaming video. To learn more and sign up now, visit: ${learnMoreUrlRef.current}. Press the back button to return to the previous page.`
      )
    }
  }, [shortCommonName, isVideoUpsell, focusSelf])

  // Key handler for Back navigation
  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = findKeyEvent(event)
      if (!key) return

      if (key === KeyTypeEnum.Back) {
        tts.stopRead()

        if (goBackUrl) {
          const hasDeeplinkOrigin = deeplinkedTo !== false && deeplinkedTo !== ''
          const detailsUrl = `/video-details/${goBackUrl}`
          setTimeout(() => {
            navigate(detailsUrl, {
              state: hasDeeplinkOrigin
                ? { origin: deeplinkedTo, fromPassportUpsell: true }
                : { fromPassportUpsell: true },
            })
          }, 0)
        } else {
          navigate(-1)
        }
      }
    },
    [goBackUrl, deeplinkedTo, navigate]
  )

  useEffect(() => {
    window.addEventListener('keydown', onKeyDownHandler, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKeyDownHandler, {
        capture: true,
      })
      tts.stopRead()
    }
  }, [onKeyDownHandler])

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="focusableDivUpsell">
        <div className="passport-upsell-container">
          <div className="upsell">
            <StationPassportTitle
              className="header"
              stationTitle={shortCommonName}
            />

            {isVideoUpsell && (
              <p className={`description ${isVideoUpsell ? 'video' : ''}`}>
                {`Watch this video with ${shortCommonName} Passport.`}
              </p>
            )}
            <p className="description">
              {`${shortCommonName} Passport is an additional member benefit that provides extended access to quality PBS streaming video.`}
            </p>
            <p className="link-text">
              {'To learn more and sign up now, visit: '}
              <span className="url-font">
                {learnMoreUrlRef.current
                  ? learnMoreUrlRef.current.replace('http://', '')
                  : ''}
              </span>
            </p>
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default PassPortUpsellTS
